import {
  type CountryCode,
  getCountryCallingCode,
  isPossiblePhoneNumber,
  isSupportedCountry,
  parsePhoneNumber,
} from "libphonenumber-js/mobile";

export { getCountryCallingCode } from "libphonenumber-js/mobile";

export const getLocaleCountry = (locale: string) => locale.slice(-2);

/*
 * Filters out countries returned by Dunlop that are not supported by libphonenumber-js
 */
export const getSupportedCountries = (
  countries: Readonly<Array<{ country: string; displayName: string }>>,
) =>
  countries
    .map(({ country, displayName }) => ({
      label: displayName,
      value: country,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
    /**
     * Array.filter is too type-strict and `isSupportedCountry` type definitions are
     * stricter than docs show (which notes string is the type of the argument.)
     *
     * @see https://gitlab.com/catamphetamine/libphonenumber-js#issupportedcountrycountry-string-boolean
     */
    .filter((country) => isSupportedCountry(country.value));

export const getPhoneNumberPrefixFromCountryCode = (
  countryCode: CountryCode | "",
): `+${string}` | undefined => {
  if (!countryCode) {
    return undefined;
  }

  return `+${getCountryCallingCode(countryCode)}`;
};

interface PhoneNumberInputs {
  countryCode: CountryCode;
  phoneNumber: string;
}

const parsePhoneNumberWithCountryCode = (
  internationalPhoneNumber: `+${string}`,
) => {
  return parsePhoneNumber(internationalPhoneNumber);
};

const getPhoneNumberWithCountryCode = ({
  countryCode,
  phoneNumber,
}: PhoneNumberInputs): `+${string}` => {
  /** "US" --> "1" */
  const countryCallingCode = getCountryCallingCode(countryCode);
  /** +14159654525 */
  return `+${countryCallingCode}${phoneNumber}`;
};

export const formatPhoneNumber = ({
  countryCode,
  phoneNumber,
}: PhoneNumberInputs) => {
  /** "US" + "4159654525" --> "+14159654525" */
  const phoneNumberWithCountryCode = getPhoneNumberWithCountryCode({
    countryCode,
    phoneNumber,
  });

  const parsedPhoneNumberWithCountryCode = parsePhoneNumberWithCountryCode(
    phoneNumberWithCountryCode,
  );

  /** +1 415 965 4525 */
  const internationalNumber =
    parsedPhoneNumberWithCountryCode.formatInternational();

  /** +1-415-965-4525 */
  const withDashes = internationalNumber.split(" ").join("-");

  /** 1-415-965-4525 */
  return withDashes.slice(1);
};

export const validatePhoneNumber = ({
  countryCode,
  phoneNumber,
}: PhoneNumberInputs) => {
  /**
   * We use `isPossiblePhoneNumber` instead of `isValidPhoneNumber` because
   * while we want some client-side number validation, we want to defer to
   * Dunlop as the source of truth. This looser validation means we avoid
   * any false positives where a number is invalid for libphonenumber-js
   * that Dunlop might consider valid. This is pre-emptive edge case handling.
   *
   * @see https://github.com/catamphetamine/libphonenumber-js#validate-phone-number
   */
  return isPossiblePhoneNumber(phoneNumber, countryCode);
};

export const validateCountryCode = (countryCode: CountryCode) => {
  return isSupportedCountry(countryCode);
};
